/**
 * A boss level's boss as a view shows it (LevelData.boss): it chases the view's racer at the
 * left of the view — huge, just behind — comes in with a roar as each attempt begins, winds up
 * as each attack's warning comes up and lets go as the attack goes off (AttackObject: the
 * level's, timed by the attempt's tick), and falls behind and crashes as the racer nears the
 * finish. Drawing only: the attacks are the level's (their physics: @cube-rush/shared); the boss
 * itself never touches anybody.
 *
 * BossWatch: the local player's sounds of it — the roar, each attack's warning and go, the crash.
 */
import type { AttackObject, BossKind, LevelData } from '@cube-rush/shared';
import type { SoundCue } from '../audio/types';

/** Where the boss is drawn: its front this far behind the racer's centre (blocks). */
export const BOSS_BEHIND: Readonly<Record<BossKind, number>> = { dragon: 1.9, robot: 2.2 };
/** Ticks an attempt's entrance takes: the boss comes in from the left edge with its roar. */
const ENTER_TICKS = 100;
/** How far behind (blocks) it starts its entrance. */
const ENTER_FROM = 7;
/**
 * It starts to lag behind this far (blocks) before the finish, LAG blocks back by the time it
 * crashes, CRASH_AT of the way — on the finish's free runway (FINISH_RUNWAY: no attacks there),
 * still in view — then falls FALL blocks more, sinking and fading, by the finish.
 */
const DEFEAT_FROM = 14;
const CRASH_AT = 0.5;
const LAG = 0.6;
const FALL = 5;
/** Ticks a wind-up takes at most (before an attack's `from`), and a let-go lasts after it. */
const WIND_UP_TICKS = 42;
const LET_GO_TICKS = 22;

/** How the boss looks this frame (see drawBoss in render/boss.ts). */
export interface BossPose {
  kind: BossKind;
  /** World x of its front (its snout, its chest) and how far it has fallen back (blocks). */
  x: number;
  /** 0..1: coming in (an attempt's start). */
  enter: number;
  /** 0..1: winding up an attack (jaw opening, eyes and emitter charging). */
  charge: number;
  /** 0..1: an attack just went off (fire from the jaw, recoil). */
  fire: number;
  /** The attack it winds up or lets go of (where the robot aims, what the dragon breathes). */
  attack: AttackObject | null;
  /** 0..1: falling behind as the racer nears the finish, then crashing (≥ CRASH_AT: crashed). */
  defeat: number;
  /** 0..1: how far it is into its crash (tumbling, sinking, fading). */
  crash: number;
  /** The racer's x: the robot's legs run with it. */
  run: number;
}

/** What happened to the boss this frame (a view shakes, throws sparks). */
export interface BossEvents {
  /** An attempt began: it comes in roaring. */
  entered: boolean;
  /** It crashed behind (the racer is about to escape it). */
  crashed: boolean;
  /** Attacks that went off since the last frame. */
  fired: AttackObject[];
}

/** The view's boss: its pose, frame by frame, from its racer's run. */
export class BossChase {
  readonly pose: BossPose = {
    kind: 'dragon',
    x: 0,
    enter: 0,
    charge: 0,
    fire: 0,
    attack: null,
    defeat: 0,
    crash: 0,
    run: 0,
  };
  private readonly events: BossEvents = { entered: false, crashed: false, fired: [] };
  private key = '';
  private lastTick = Number.NEGATIVE_INFINITY;
  private crashed = false;

  /**
   * The boss's pose for a racer at `x` at the attempt's tick `tick` (fractional) on `level` (its
   * attacks: `attacks`), in the attempt known by `attemptKey`. Null on a level without a boss.
   */
  update(
    level: LevelData,
    attacks: readonly AttackObject[],
    attemptKey: string,
    x: number,
    tick: number,
  ): BossEvents | null {
    const events = this.events;
    events.entered = false;
    events.crashed = false;
    events.fired.length = 0;
    const kind = level.boss;
    if (kind === undefined) return null;
    if (attemptKey !== this.key) {
      this.key = attemptKey;
      this.crashed = false;
      this.lastTick = tick;
      events.entered = tick < ENTER_TICKS;
    }
    const pose = this.pose;
    pose.kind = kind;
    pose.run = x;
    pose.enter = clamp01(tick / ENTER_TICKS);
    const defeat = clamp01((DEFEAT_FROM - (level.length - x)) / DEFEAT_FROM);
    const lag = Math.min(1, defeat / CRASH_AT);
    const crash = clamp01((defeat - CRASH_AT) / (1 - CRASH_AT));
    pose.defeat = defeat;
    pose.crash = crash;
    // In from the left, then right behind the racer; lagging at the end, then left behind.
    const entering = 1 - easeOut(pose.enter);
    const back = LAG * lag * lag + FALL * crash * crash;
    pose.x = x - BOSS_BEHIND[kind] - ENTER_FROM * entering - back;
    // The attack it winds up (its warning is up) or has just let go of.
    let charge = 0;
    let fire = 0;
    let attack: AttackObject | null = null;
    for (const a of attacks) {
      if (tick < a.warn || tick > a.from + LET_GO_TICKS) continue;
      if (tick < a.from) {
        const span = Math.max(1, Math.min(WIND_UP_TICKS, a.from - a.warn));
        const c = clamp01((tick - (a.from - span)) / span);
        if (c > charge) {
          charge = c;
          attack = a;
        }
      } else {
        const f = 1 - (tick - a.from) / LET_GO_TICKS;
        if (f > fire) {
          fire = f;
          if (charge === 0) attack = a;
        }
      }
      if (a.from > this.lastTick && a.from <= tick) events.fired.push(a);
    }
    pose.charge = charge;
    pose.fire = fire;
    pose.attack = attack;
    if (!this.crashed && pose.defeat >= CRASH_AT) {
      this.crashed = true;
      events.crashed = true;
    }
    this.lastTick = tick;
    return events;
  }
}

/**
 * The local player's sounds of the boss: its roar as its level begins (a growl as it comes back
 * for another attempt), each attack's warning and go as the run's tick passes them, its crash as
 * the player escapes it. Fed the run's whole ticks; once per tick each.
 */
export class BossWatch {
  private level = -1;
  private attempt = -1;
  private lastTick = 0;
  private crashed = false;
  private readonly cues: SoundCue[] = [];

  /**
   * The cues due as the run of level `index` (its level data `level`, its attacks `attacks`),
   * attempt `attempt`, comes to tick `tick` at x.
   */
  check(
    index: number,
    level: LevelData,
    attacks: readonly AttackObject[],
    attempt: number,
    tick: number,
    x: number,
  ): readonly SoundCue[] {
    const cues = this.cues;
    cues.length = 0;
    const boss = level.boss;
    if (boss === undefined) return cues;
    if (index !== this.level || attempt !== this.attempt) {
      cues.push({ kind: 'boss', boss, event: index !== this.level ? 'roar' : 'growl' });
      this.level = index;
      this.attempt = attempt;
      this.lastTick = tick;
      this.crashed = false;
    }
    for (const a of attacks) {
      if (a.warn > this.lastTick && a.warn <= tick) {
        cues.push({ kind: 'attack', attack: a.attack, warn: true });
      }
      if (a.from > this.lastTick && a.from <= tick) {
        cues.push({ kind: 'attack', attack: a.attack, warn: false });
      }
    }
    if (!this.crashed && level.length - x <= DEFEAT_FROM * (1 - CRASH_AT)) {
      this.crashed = true;
      cues.push({ kind: 'boss', boss, event: 'defeat' });
    }
    this.lastTick = tick;
    return cues;
  }
}

function clamp01(v: number): number {
  return Math.min(1, Math.max(0, v));
}

function easeOut(t: number): number {
  return 1 - (1 - t) * (1 - t) * (1 - t);
}
